import { SectionHeader, Reveal } from "@/components/SectionHeader";
import SEO from "@/components/SEO";
import { ShieldCheck, Microscope, FlaskConical, FileText, Award, Eye } from "lucide-react";

const LAB = "https://images.unsplash.com/photo-1518152006812-edab29b069ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxzY2llbnRpZmljJTIwbGFiJTIwZXF1aXBtZW50JTIwbWljcm9zY29wZXxlbnwwfHx8fDE3ODI3NTAzMzl8MA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: ShieldCheck, title: "Independent", body: "We don't remediate. Ever. That single rule removes every conflict of interest in the industry." },
  { icon: Microscope, title: "Scientific", body: "Every finding is tied to a measurable observation, a calibrated instrument, or an accredited laboratory result." },
  { icon: FileText, title: "Transparent", body: "Our reports cite the standard, the method, and the limitation. There is no hidden interpretation." },
  { icon: FlaskConical, title: "Educational", body: "We explain what we found, what it means, and what it does not mean — in plain English." },
  { icon: Award, title: "Premium", body: "Texas-proud, Fortune-500 caliber consulting. Built to be the most respected environmental firm in the state." },
  { icon: Eye, title: "Consultative", body: "Our value is what happens after the report — guiding clients through decisions, protocols, and verification." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About — Independent Texas Environmental Consulting"
        description="Hurtler Environmental is independent by design. We never remediate. Learn how this single business rule shapes every assessment we deliver across Texas."
        path="/about"
      />
      <section className="bg-[#0F172A] text-white py-24 md:py-32">
        <div className="container-x">
          <div className="overline mb-6 text-[#F59E0B]">About Hurtler</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
            The independent environmental consulting firm Texas has been waiting for.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
            Headquartered in Edinburg, Texas — built to grow into the most respected environmental
            assessment company in the state.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <SectionHeader
              overline="The Hurtler difference"
              title="Most environmental firms also sell remediation. We don't."
              subtitle="That single business decision changes every incentive in the inspection process. We have no scope to expand, no contract to sell, and no clearance bias. Our entire product is honest data and a defensible interpretation."
            />
            <p className="mt-6 text-slate-600 leading-relaxed">
              We serve homeowners, commercial property managers, schools, healthcare facilities,
              attorneys, insurance carriers, restoration contractors, realtors, and government
              agencies. Each engagement is grounded in the same scientific method and reported
              with the same transparency.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={LAB} alt="Lab equipment" className="w-full h-[460px] object-cover rounded-2xl" />
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-[#F8FAFC]">
        <div className="container-x">
          <SectionHeader overline="Our principles" title="Six commitments that shape every engagement." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.05}>
                  <div data-testid={`about-value-${i}`} className="card-soft p-8 h-full">
                    <Icon className="w-7 h-7 text-[#7C3AED] mb-5" />
                    <h3 className="font-display font-bold text-lg text-[#0F172A]">{v.title}</h3>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
